import * as React from "react";
import { Moon, Sun, Laptop } from "lucide-react";
import { Button } from "./button.js";
import { useTheme, type Theme } from "./theme-provider.js";
import { cn } from "../lib/cn.js";

/**
 * Tri-state theme switcher (light/system/dark) — single button cycles
 * forward, icon reflects current preference. Honours `prefers-reduced-
 * motion` by skipping the rotate animation.
 */
const ORDER: readonly Theme[] = ["light", "system", "dark"];

export interface ThemeSwitcherProps extends React.ComponentProps<typeof Button> {
  /** Visually hidden label for screen readers. */
  srLabel?: string;
}

export function ThemeSwitcher({
  className,
  srLabel = "Toggle theme",
  ...props
}: ThemeSwitcherProps) {
  const { theme, setTheme } = useTheme();
  const Icon = theme === "light" ? Sun : theme === "dark" ? Moon : Laptop;

  const next = () => {
    const i = ORDER.indexOf(theme);
    setTheme(ORDER[(i + 1) % ORDER.length] ?? "system");
  };

  return (
    <Button
      variant="ghost"
      size="icon"
      aria-label={srLabel}
      title={`Theme: ${theme}`}
      onClick={next}
      className={cn(
        "transition-transform hover:rotate-12 motion-reduce:transition-none",
        className,
      )}
      {...props}
    >
      <Icon aria-hidden="true" />
    </Button>
  );
}
